import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminHeader from '../../../components/admin/AdminHeader';
import PointsUsersTab from '../../../components/admin/points/PointsUsersTab';
import PointsTransactionsTab from '../../../components/admin/points/PointsTransactionsTab';
import PointsRulesTab from '../../../components/admin/points/PointsRulesTab';
import api from '../../../services/api';
import authService from '../../../services/authService';

const PointsManagementPage = () => {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [adminUser, setAdminUser] = useState(null);
  const navigate = useNavigate();
  
  useEffect(() => {
    // Check if user is logged in as admin using authService
    if (!authService.isAdminLoggedIn()) {
      console.log('PointsManagementPage - Not authenticated, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    // Get admin user data from authService
    const userData = authService.getAdminUser();
    
    if (!userData) {
      console.log('PointsManagementPage - No admin user data, redirecting to login');
      navigate('/admin/login');
      return;
    }
    
    console.log('PointsManagementPage - User data:', userData);
    setAdminUser(userData);
    
    // Load summary data when the admin user is loaded
    fetchSummary();
  }, [navigate]);

  const fetchSummary = async () => {
    setLoading(true);
    setError(null);

    try {
      console.log('PointsManagementPage - Fetching points summary');
      
      // Use our centralized API service which handles auth tokens automatically
      const response = await api.getPointsSummary();
      
      console.log('PointsManagementPage - Summary data:', response.data);
      setSummary(response.data.data);
    } catch (err) {
      console.error('Error fetching summary:', err);
      
      // Check for auth errors
      if (err.response && (err.response.status === 401 || err.response.status === 403)) {
        console.error('Authentication error:', err.response.status);
        
        // Clear credentials and redirect to login using authService
        authService.adminLogout();
        navigate('/admin/login');
        return;
      }
      
      setError('Failed to load summary data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'summary':
        return renderSummary();
      case 'users':
        return <PointsUsersTab />;
      case 'transactions':
        return <PointsTransactionsTab />;
      case 'rules':
        return <PointsRulesTab />;
      default:
        return renderSummary();
    }
  };

  const renderSummary = () => {
    if (loading) {
      return <div className="loading">Loading summary data...</div>;
    }

    if (error) {
      return <div className="error">{error}</div>;
    }

    if (!summary) {
      return <div>No summary data available</div>;
    }

    return (
      <div>
        <h3>Points System Summary</h3>
        
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', marginBottom: '30px' }}>
          {/* Summary cards */}
          <SummaryCard 
            title="Total Users" 
            value={summary.totalUsers} 
            icon="👤"
          />
          <SummaryCard 
            title="Total Points" 
            value={summary.totalPoints.toLocaleString()} 
            icon="🔢"
          />
          <SummaryCard 
            title="Total Transactions" 
            value={summary.totalTransactions.toLocaleString()} 
            icon="🔄"
          />
          <SummaryCard 
            title="Active Rules" 
            value={summary.activeRules} 
            icon="📏"
          />
        </div>

        <h3>Points Activity</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px' }}>
          {/* Activity cards */}
          <ActivityCard 
            title="Points Earned" 
            value={summary.pointsEarned.toLocaleString()} 
            count={summary.earnTransactions} 
            color="#4CAF50"
          />
          <ActivityCard 
            title="Points Spent" 
            value={summary.pointsSpent.toLocaleString()} 
            count={summary.spendTransactions} 
            color="#F44336"
          />
        </div>
        
        <div style={{ marginTop: '30px' }}>
          <button 
            onClick={() => setActiveTab('users')} 
            style={actionButtonStyle}
          >
            View Users
          </button>
          <button 
            onClick={() => setActiveTab('transactions')} 
            style={actionButtonStyle}
          >
            View Transactions
          </button>
          <button 
            onClick={() => setActiveTab('rules')} 
            style={actionButtonStyle}
          >
            Manage Rules
          </button>
        </div>
      </div>
    );
  };

  // Reusable UI components
  const SummaryCard = ({ title, value, icon }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      width: '200px',
      textAlign: 'center'
    }}>
      <div style={{ fontSize: '36px', marginBottom: '10px' }}>{icon}</div>
      <div style={{ fontSize: '24px', fontWeight: 'bold' }}>{value}</div>
      <div style={{ color: '#666' }}>{title}</div>
    </div>
  );

  const ActivityCard = ({ title, value, count, color }) => (
    <div style={{
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      width: '280px',
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
        <div style={{ fontWeight: 'bold', fontSize: '16px' }}>{title}</div>
        <div style={{ 
          backgroundColor: color, 
          color: 'white', 
          borderRadius: '12px',
          padding: '2px 10px',
          fontSize: '12px'
        }}>
          {count} transactions
        </div>
      </div>
      <div style={{ fontSize: '28px', fontWeight: 'bold', color }}>{value}</div>
    </div>
  );

  // Styles
  const tabStyle = {
    padding: '12px 24px',
    cursor: 'pointer',
    borderBottom: '3px solid transparent',
    fontSize: '14px',
    fontWeight: '500',
    color: '#6b7280',
    transition: 'all 0.2s',
    backgroundColor: 'transparent'
  };

  const activeTabStyle = {
    ...tabStyle,
    borderBottomColor: '#4f46e5',
    color: '#4f46e5'
  };

  const actionButtonStyle = {
    backgroundColor: '#4f46e5',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    padding: '10px 16px',
    margin: '0 10px 10px 0',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: '500'
  };

  if (!adminUser) {
    return <div>Loading admin dashboard...</div>;
  }

  return (
    <div>
      <AdminHeader adminUser={adminUser} />
      <div style={{ 
        maxWidth: '1200px', 
        margin: '0 auto', 
        padding: '20px' 
      }}>
        <h2 style={{ 
          margin: '0 0 8px 0', 
          fontSize: '28px', 
          color: '#111827' 
        }}>
          Points Management
        </h2>
        <p style={{ 
          margin: '0 0 24px 0', 
          fontSize: '14px', 
          color: '#6b7280' 
        }}>
          Manage points, transactions, and reward rules
        </p>
        
        {/* Tab Navigation */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '8px 8px 0 0',
          borderBottom: 'none',
          display: 'flex',
          gap: '8px',
          padding: '0 20px'
        }}>
          <div
            onClick={() => setActiveTab('summary')}
            style={activeTab === 'summary' ? activeTabStyle : tabStyle}
          >
            Summary
          </div>
          <div
            onClick={() => setActiveTab('users')}
            style={activeTab === 'users' ? activeTabStyle : tabStyle}
          >
            Users
          </div>
          <div
            onClick={() => setActiveTab('transactions')}
            style={activeTab === 'transactions' ? activeTabStyle : tabStyle}
          >
            Transactions
          </div>
          <div
            onClick={() => setActiveTab('rules')}
            style={activeTab === 'rules' ? activeTabStyle : tabStyle}
          >
            Rules
          </div>
        </div>
        
        {/* Tab Content */}
        <div style={{
          backgroundColor: '#ffffff',
          border: '1px solid #e5e7eb',
          borderRadius: '0 0 8px 8px',
          padding: '24px',
          minHeight: '400px'
        }}>
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default PointsManagementPage;